import { Link } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { GestureResponderEvent, ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AddGiftCardBanner, Button, IconAdd, YStack, tokens, useMqSelect } from '@fhs/ui';
import { Divider } from '@fhs-legacy/universal-components';
import LoadingAnimation from 'components/loading-animation';
import PaymentMethodOption from 'components/payment-method-option';
import UserVerificationModal from 'components/user-verification-modal';
import useUserHasToVerify from 'hooks/use-user-has-to-verify';
import { usePaymentContext } from 'state/payment';
import { PAYPAL_PAYMENT_METHOD_PLACEHOLDER } from 'state/payment/constants';
import { IPaymentMethod } from 'state/payment/types';
import { useFlag } from 'utils/launchdarkly';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';
import { routes } from 'utils/routing';

import { filterPaymentMethods } from './filter-payment-methods';
import { getPaymentMethodOptionProps } from './get-payment-method-options-props';
import { DefaultText, DefaultTextWrapper, Form, Message } from './styled';

function filterOutUnsupportedPaymentMethods(paymentMethods: IPaymentMethod[]) {
  // Exclude PayPal if it is not actually stored in the user account
  return paymentMethods.filter(
    n => n.accountIdentifier !== PAYPAL_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier
  );
}

const isNativePay = (m: IPaymentMethod) => {
  const id = m.accountIdentifier ?? m.fdAccountId ?? '';

  return (
    isApplePay(id) ||
    isGooglePay(id) ||
    m.credit?.cardType === 'APPLE_PAY' ||
    m.credit?.cardType === 'GOOGLE_PAY'
  );
};

// Define sorting priorities:
// 0 -> regular (including cash)
// 1 -> native pay (Google / Apple Pay)
// 2 -> gift cards
function getPaymentPriority(method: IPaymentMethod) {
  if (method.giftCard) {
    return 2;
  }

  if (isNativePay(method)) {
    return 1;
  }

  return 0;
}

const PaymentMethodFlatListWithButton: React.FC<React.PropsWithChildren<unknown>> = () => {
  const {
    deletePaymentMethod,
    loading,
    paymentMethods,
    setDefaultReloadPaymentMethodId,
    prepaidReloadPaymentMethodId,
    setDefaultPaymentMethodId,
    defaultPaymentMethodId,
    isUpdatingPaymentMethod,
  } = usePaymentContext();
  const { formatMessage } = useIntl();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const enableDoubleVaultingFromPaymentMethods = useFlag(
    'enable-double-vaulting-from-payment-methods'
  );
  const enableUnverifiedSavedPayments = useFlag('enable-unverified-saved-payments');
  const enableGiftCards = useFlag('enable-fhs-gift-cards');
  const userHasToVerify = useUserHasToVerify(enableUnverifiedSavedPayments);
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);

  // Filter the payment methods per brands
  const filteredPaymentMethods = filterPaymentMethods({
    paymentMethods: filterOutUnsupportedPaymentMethods(paymentMethods),
  });

  const hasPaymentMethods = filteredPaymentMethods.length > 0;

  const mappedPaymentMethods = useMemo(() => {
    // Filter out gift cards if the feature is disabled
    const methodsToDisplay = enableGiftCards
      ? filteredPaymentMethods
      : filteredPaymentMethods.filter(method => !method.giftCard);

    // Sort payment methods while keeping the original order within each group
    const sortedPaymentMethods = methodsToDisplay
      .map((method, originalIndex) => ({ method, originalIndex }))
      .sort((a, b) => {
        const aPriority = getPaymentPriority(a.method);
        const bPriority = getPaymentPriority(b.method);

        if (aPriority === bPriority) {
          return a.originalIndex - b.originalIndex;
        }

        return aPriority - bPriority;
      })
      .map(({ method }) => method);

    return sortedPaymentMethods.map(method => {
      const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
      const isNativePayment = isApplePay(accountIdentifier) || isGooglePay(accountIdentifier);

      const isCashPayment = !!method.cash;

      const paymentMethodOptionProps = getPaymentMethodOptionProps({
        isNativePayment,
        prepaidReloadPaymentMethodId,
        setDefaultReloadPaymentMethodId,
        defaultPaymentMethodId,
        setDefaultPaymentMethodId,
        accountIdentifier,
        enableDoubleVaultingFromPaymentMethods,
        method,
      });

      return (
        <View key={accountIdentifier}>
          <PaymentMethodOption
            key={accountIdentifier}
            method={method}
            showDefault
            removable={!method.prepaid && !isNativePayment && !isCashPayment}
            readonly={isNativePayment}
            disablePress={isUpdatingPaymentMethod}
            deletePaymentMethod={deletePaymentMethod}
            isDefaultPaymentMethod={accountIdentifier === defaultPaymentMethodId}
            fullWidthDescription
            {...paymentMethodOptionProps}
          />
          <Divider />
        </View>
      );
    });
  }, [
    defaultPaymentMethodId,
    deletePaymentMethod,
    enableDoubleVaultingFromPaymentMethods,
    enableGiftCards,
    filteredPaymentMethods,
    isUpdatingPaymentMethod,
    prepaidReloadPaymentMethodId,
    setDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId,
  ]);

  const handleAddCard = (event: GestureResponderEvent) => {
    if (userHasToVerify) {
      event?.stopPropagation();
      setShowVerificationModal(true);
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <View
        testID="account-payment-methods-page"
        style={{
          padding: 16,
          flex: 1,
          justifyContent: 'space-between',
        }}
      >
        {loading ? (
          <LoadingAnimation />
        ) : (
          <>
            <ScrollView style={{ flex: 1 }} removeClippedSubviews={false}>
              {!hasPaymentMethods ? (
                <Message>{formatMessage({ id: 'noPaymentMethodOnFile' })}</Message>
              ) : (
                <>
                  <DefaultTextWrapper>
                    <DefaultText>{formatMessage({ id: 'accountDefault' })}</DefaultText>
                  </DefaultTextWrapper>
                  <Form testID="account-saved-payments">{mappedPaymentMethods}</Form>
                </>
              )}
              {enableGiftCards && (
                <View style={{ marginVertical: 32 }}>
                  <AddGiftCardBanner />
                </View>
              )}
            </ScrollView>
            {isDesktop && (
              <View style={{ marginTop: 32 }}>
                <Link href={!userHasToVerify ? routes.addCard : routes.payment} asChild>
                  <Button size="xl" onPress={handleAddCard} testID="add-new-payment-method">
                    <Button.Icon>
                      <IconAdd />
                    </Button.Icon>
                    <Button.Text>New Credit/Debit Card</Button.Text>
                  </Button>
                </Link>
              </View>
            )}
          </>
        )}
      </View>
      {!isDesktop && (
        <SafeAreaView edges={['bottom']} style={{ backgroundColor: 'white' }}>
          <YStack
            style={{
              width: '100%',
              backgroundColor: 'white',
              paddingHorizontal: 16,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderColor: tokens.colors.$blackOpacity10,
              flexShrink: 0,
            }}
          >
            <Link href={!userHasToVerify ? routes.addCard : routes.payment} asChild>
              <Button size="xl" onPress={handleAddCard} testID="add-new-payment-method">
                <Button.Icon>
                  <IconAdd />
                </Button.Icon>
                <Button.Text>New Credit/Debit Card</Button.Text>
              </Button>
            </Link>
          </YStack>
        </SafeAreaView>
      )}

      {showVerificationModal && (
        <UserVerificationModal showDialog onDismiss={() => setShowVerificationModal(false)} />
      )}
    </View>
  );
};

export default PaymentMethodFlatListWithButton;
